import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Trophy, Target, Gamepad2, Award, MapPin, Calendar,
  ChevronRight, Mail, Sparkles, History, Pencil, Check, X,
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useApi } from '../../hooks/useApi';
import { useLevels } from '../../hooks/useLevels';
import { iconFor, getLevelForScore, getNextLevel, getLevelProgress } from '../../utils/levels';
import { AVATAR_IDS, avatarSrc, DEFAULT_AVATAR_ID } from '../avatar/avatarList';
import RanksDialog from '../ui/RanksDialog';
import './Profile.css';

const useAnimatedNumber = (end, duration = 1200) => {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!end) { setValue(0); return; }
    let start = null;
    let rafId;
    const step = (ts) => {
      if (!start) start = ts;
      const progress = Math.min((ts - start) / duration, 1);
      setValue(Math.floor(progress * end));
      if (progress < 1) rafId = window.requestAnimationFrame(step);
    };
    rafId = window.requestAnimationFrame(step);
    return () => window.cancelAnimationFrame(rafId);
  }, [end, duration]);
  return value;
};

const formatDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

const memberSince = (iso) => {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
};

const StatCard = ({ icon: Icon, label, value, accent, suffix }) => (
  <div className="pf-stat-card">
    <div className="pf-stat-icon" style={{ color: accent, background: `${accent}1f`, borderColor: `${accent}33` }}>
      <Icon size={18} strokeWidth={2} />
    </div>
    <div className="pf-stat-body">
      <span className="pf-stat-value">
        {value}
        {suffix && <span className="pf-stat-suffix">{suffix}</span>}
      </span>
      <span className="pf-stat-label">{label}</span>
    </div>
  </div>
);

const Profile = () => {
  const { user, updateUser } = useAuth();
  const { request } = useApi();
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [ranksOpen, setRanksOpen] = useState(false);

  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editAvatar, setEditAvatar] = useState(DEFAULT_AVATAR_ID);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const startEdit = () => {
    setEditName(user?.name || '');
    setEditAvatar(user?.avatar || DEFAULT_AVATAR_ID);
    setSaveError('');
    setEditing(true);
  };

  const cancelEdit = () => { setEditing(false); setSaveError(''); };

  const saveEdit = async () => {
    if (!editName.trim()) return;
    setSaving(true);
    setSaveError('');
    try {
      await updateUser({ name: editName.trim(), avatar: editAvatar });
      setEditing(false);
    } catch (e) {
      setSaveError(e?.response?.data?.message || 'Failed to save. Try again.');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    request('get', '/game/stats').then(setStats).catch(() => {});
    request('get', '/user/game-history')
      .then((res) => setHistory(res?.data ?? []))
      .catch(() => setHistory([]))
      .finally(() => setHistoryLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const s = stats?.stats;
  const totalScore  = s?.total_score      ?? user?.total_score   ?? 0;
  const gamesPlayed = s?.total_games      ?? user?.games_played  ?? 0;
  const bestScore   = s?.best_score       ?? user?.best_score    ?? 0;
  const avgScore    = s?.average_score    ?? 0;
  const avgDist     = s?.average_distance ?? 0;

  const animTotal = useAnimatedNumber(totalScore);
  const animGames = useAnimatedNumber(gamesPlayed);
  const animBest  = useAnimatedNumber(bestScore);
  const animAvg   = useAnimatedNumber(Math.round(avgScore));

  const { levels } = useLevels();
  const rank      = getLevelForScore(levels, totalScore) ?? { name: '—', color: '#64748b', icon: 'trophy' };
  const nextRank  = getNextLevel(levels, totalScore);
  const progress  = getLevelProgress(levels, totalScore);
  const RankIcon  = iconFor(rank.icon);
  const NextIcon  = nextRank ? iconFor(nextRank.icon) : null;
  const initials  = user?.name?.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase() || 'P';
  const joined    = memberSince(user?.created_at);

  return (
    <div className="pf">
      <div className="pf-container">

        {/* ── Identity hero ── */}
        <section className="pf-hero">
          <div className="pf-hero-glow" style={{ background: `radial-gradient(circle at 30% 30%, ${rank.color}22, transparent 60%)` }} />
          <div className="pf-hero-main">
            <div className="pf-avatar-wrap">
              <div className="pf-avatar" style={{ borderColor: rank.color }}>
                {(editing ? editAvatar : user?.avatar)
                  ? <img src={avatarSrc(editing ? editAvatar : user.avatar)} alt={user?.name} />
                  : <span>{initials}</span>}
              </div>
              <div className="pf-avatar-badge" style={{ background: rank.color }}>
                <RankIcon size={12} strokeWidth={2.5} />
              </div>
            </div>

            <div className="pf-identity">
              {editing ? (
                <div className="pf-edit-form">
                  <div className="pf-edit-avatars">
                    {AVATAR_IDS.map(id => (
                      <button
                        key={id}
                        type="button"
                        className={`pf-edit-avatar-btn${editAvatar === id ? ' selected' : ''}`}
                        onClick={() => setEditAvatar(id)}
                        aria-label={id}
                      >
                        <img src={avatarSrc(id)} alt="" />
                      </button>
                    ))}
                  </div>
                  <input
                    className="pf-edit-input"
                    value={editName}
                    onChange={e => setEditName(e.target.value)}
                    placeholder="Your name"
                    maxLength={50}
                    autoFocus
                  />
                  {saveError && <p className="pf-edit-error">{saveError}</p>}
                  <div className="pf-edit-actions">
                    <button
                      type="button"
                      className="pf-edit-save"
                      onClick={saveEdit}
                      disabled={saving || !editName.trim()}
                    >
                      <Check size={14} />{saving ? 'Saving…' : 'Save'}
                    </button>
                    <button type="button" className="pf-edit-cancel" onClick={cancelEdit}>
                      <X size={14} />Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <span className="pf-eyebrow">Player Profile</span>
                  <div className="pf-name-row">
                    <h1 className="pf-name">{user?.name || 'Explorer'}</h1>
                    <button type="button" className="pf-edit-btn" onClick={startEdit} aria-label="Edit profile">
                      <Pencil size={14} />
                    </button>
                  </div>
                  <div className="pf-meta">
                    <span className="pf-meta-item">
                      <Mail size={13} /> {user?.email}
                    </span>
                    {joined && (
                      <span className="pf-meta-item">
                        <Calendar size={13} /> Joined {joined}
                      </span>
                    )}
                  </div>
                  <button
                    type="button"
                    className="pf-rank-pill rd-trigger"
                    style={{ color: rank.color, borderColor: `${rank.color}55`, background: `${rank.color}14` }}
                    onClick={() => setRanksOpen(true)}
                    aria-label={`Current rank ${rank.name}. View all rank tiers.`}
                  >
                    <RankIcon size={13} strokeWidth={2.4} />
                    {rank.name}
                  </button>
                </>
              )}
            </div>
          </div>

          <Link to="/game" className="pf-cta">
            <Gamepad2 size={17} />
            Play a round
          </Link>
        </section>

        {/* ── Rank progress ── */}
        <section className="pf-progress">
          <div className="pf-progress-head">
            <button
              type="button"
              className="pf-progress-current rd-trigger"
              style={{ color: rank.color }}
              onClick={() => setRanksOpen(true)}
              aria-label={`Current rank ${rank.name}. View all rank tiers.`}
            >
              <RankIcon size={13} /> {rank.name}
            </button>
            {nextRank ? (
              <span className="pf-progress-next">
                <span style={{ color: nextRank.color }}>
                  <NextIcon size={12} /> {nextRank.name}
                </span>
                <span className="pf-progress-gap">· {(nextRank.min_points - totalScore).toLocaleString()} pts to go</span>
              </span>
            ) : (
              <span className="pf-progress-next">
                <Sparkles size={12} style={{ color: rank.color }} /> Max rank reached
              </span>
            )}
          </div>
          <div className="pf-progress-bar">
            <div className="pf-progress-fill" style={{ width: `${progress}%`, background: rank.color }} />
          </div>
        </section>

        {/* ── Stats grid ── */}
        <section className="pf-stats">
          <StatCard icon={Trophy}   label="Total Score"     value={animTotal.toLocaleString()} accent="#f59e0b" />
          <StatCard icon={Gamepad2} label="Games Played"    value={animGames}                  accent="#14b8a6" />
          <StatCard icon={Award}    label="Best Score"      value={animBest.toLocaleString()}  accent="#8b5cf6" />
          <StatCard icon={Target}   label="Average Score"   value={animAvg.toLocaleString()}   accent="#3b82f6" />
          <StatCard icon={MapPin}   label="Avg Distance"    value={avgDist.toFixed(1)}         suffix=" km" accent="#ef4444" />
        </section>

        {/* ── Recent games ── */}
        <section className="pf-history">
          <header className="pf-section-head">
            <div>
              <h2 className="pf-section-title"><History size={16} /> Recent Games</h2>
              <p className="pf-section-sub">Your last completed sessions</p>
            </div>
            <Link to="/leaderboard" className="pf-section-link">
              View leaderboard <ChevronRight size={14} />
            </Link>
          </header>

          {historyLoading ? (
            <div className="pf-history-list">
              {[1,2,3].map(i => <div key={i} className="pf-history-skeleton" />)}
            </div>
          ) : history.length === 0 ? (
            <div className="pf-empty">
              <Gamepad2 size={28} className="pf-empty-icon" />
              <p>No games yet — your history will appear here once you finish a round.</p>
              <Link to="/game" className="pf-empty-cta">Start your first game</Link>
            </div>
          ) : (
            <ul className="pf-history-list">
              {history.slice(0, 6).map((g) => {
                const isBest = g.total_score === bestScore && bestScore > 0;
                return (
                  <li key={g.id} className={`pf-history-row${isBest ? ' is-best' : ''}`}>
                    <div className="pf-history-left">
                      <div className="pf-history-icon">
                        <MapPin size={15} />
                      </div>
                      <div className="pf-history-info">
                        <div className="pf-history-title">
                          {g.locations?.length
                            ? g.locations.map(l => l.name).join(' · ')
                            : `${g.completed_rounds}-round session`}
                        </div>
                        <div className="pf-history-meta">
                          <span>{formatDate(g.created_at)}</span>
                          <span className="pf-dot" />
                          <span>{g.completed_rounds}/{g.total_rounds} rounds</span>
                          {g.average_distance > 0 && (
                            <>
                              <span className="pf-dot" />
                              <span>{Number(g.average_distance).toFixed(1)} km avg</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="pf-history-right">
                      {isBest && <span className="pf-best-tag"><Award size={11} /> Best</span>}
                      <div className="pf-history-score">
                        <span className="pf-history-score-num">{Number(g.total_score).toLocaleString()}</span>
                        <span className="pf-history-score-label">pts</span>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

      </div>

      <RanksDialog
        open={ranksOpen}
        onClose={() => setRanksOpen(false)}
        currentScore={totalScore}
      />
    </div>
  );
};

export default Profile;